import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import { Clock4, ArrowRight } from "lucide-react";

function formatRelative(iso, lang = "es") {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return ({ es: "ahora", en: "now", fr: "maintenant", ca: "ara" }[lang] || "ahora");
  if (mins < 60) {
    const u = { es: "min", en: "min", fr: "min", ca: "min" }[lang] || "min";
    return `${mins} ${u}`;
  }
  const hrs = Math.round(mins / 60);
  if (hrs < 24) {
    const u = { es: "h", en: "h", fr: "h", ca: "h" }[lang] || "h";
    return `${hrs} ${u}`;
  }
  const days = Math.round(hrs / 24);
  const u = { es: "d", en: "d", fr: "j", ca: "d" }[lang] || "d";
  return `${days} ${u}`;
}

export default function SavedAnalysisBanner({ savedAt, fileName, onResume }) {
  const { t, lang } = useLang();

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center px-4 sm:top-5 md:top-6"
      data-testid="saved-analysis-banner"
    >
      <button
        type="button"
        onClick={onResume}
        className="pointer-events-auto inline-flex max-w-[min(100%,18rem)] items-center justify-center gap-1.5 overflow-hidden rounded-full bg-white px-3.5 py-2 text-[11px] font-semibold text-zinc-900 shadow-md transition hover:bg-[color:var(--sentinel-tint)] sm:px-4 sm:py-2.5 sm:text-xs md:hidden"
        data-testid="saved-analysis-resume"
      >
        <span className="truncate">{t.savedBanner.resumeShort}</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
      </button>

      <div className="pointer-events-auto hidden w-full max-w-xl items-center gap-3 rounded-2xl border border-white/25 bg-white/95 p-3 pl-5 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.45)] backdrop-blur-md md:flex">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--sentinel-tint)] text-[color:var(--sentinel)]">
          <Clock4 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900">{t.savedBanner.title}</p>
          <p className="truncate text-xs text-zinc-500">
            {fileName || "report.pdf"} · {t.savedBanner.savedAgo} {formatRelative(savedAt, lang)}
          </p>
        </div>
        <button
          type="button"
          onClick={onResume}
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-zinc-900 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-[color:var(--sentinel)]"
          data-testid="saved-analysis-resume-desktop"
        >
          {t.savedBanner.resume}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
}
